import React, { useRef, useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import {
  WorkflowModalBody,
  WorkflowModalFooter,
  WorkflowModalHeader,
  WorkflowModalShell,
} from "../workflow/WorkflowModal";
import { ReadableNavButton } from "../Sticker";
import { dismissPushPrompt } from "../../lib/pushPromptDismiss";
import { subscribeToPush } from "../../lib/webPush";

export default function PushNotificationPromptDialog({
  open,
  dancerId,
  onClose,
}) {
  const dialogRef = useRef(null);
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  async function handleEnable() {
    if (!dancerId || busy) return;
    setBusy(true);
    try {
      const result = await subscribeToPush(dancerId);
      if (result.ok) {
        toast.success("Browser notifications enabled.");
        onClose?.({ enabled: true });
        return;
      }
      if (result.reason === "denied") {
        toast.error("Notifications blocked — enable them in your browser settings.");
      }
      onClose?.({ enabled: false });
    } finally {
      setBusy(false);
    }
  }

  function handleLater() {
    dismissPushPrompt(dancerId);
    onClose?.({ enabled: false });
  }

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={handleLater}
      testId="push-notification-prompt-dialog"
      titleId="push-notification-prompt-title"
      maxWidthClass="sm:max-w-[480px]"
    >
      <WorkflowModalHeader
        eyebrow={<Bell className="w-4 h-4" strokeWidth={2.5} aria-hidden />}
        title="Get updates in your browser?"
        titleId="push-notification-prompt-title"
        onClose={handleLater}
        closeTestId="push-notification-prompt-close"
      />
      <WorkflowModalBody>
        <p className="font-ui font-medium text-[15px] text-ink/80 leading-relaxed">
          Turn on browser notifications for host messages, schedule changes, and day-of RSVP reminders —
          even when this tab is in the background.
        </p>
      </WorkflowModalBody>
      <WorkflowModalFooter className="flex flex-col sm:flex-row gap-2">
        <ReadableNavButton
          type="button"
          variant="primary"
          onClick={handleEnable}
          disabled={busy}
          className="w-full sm:flex-1"
          data-testid="push-notification-prompt-enable"
        >
          {busy ? "Enabling…" : "Enable notifications"}
        </ReadableNavButton>
        <ReadableNavButton
          type="button"
          onClick={handleLater}
          disabled={busy}
          className="w-full sm:flex-1"
          data-testid="push-notification-prompt-later"
        >
          Not now
        </ReadableNavButton>
      </WorkflowModalFooter>
    </WorkflowModalShell>
  );
}
